import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';
import { showingEntriesLabel } from '@/pages/browse/toolbar-actions/entryCountLabels';
import { cn } from '@/services/utils';

export interface FoundItemsLabelProps {
    visibleRowCount: number;
    numberFound: number;
    maxNumberFound: number;
    countingItems?: boolean;
    hasCursor?: boolean;
    onRefreshCount?: () => void;
    /** The exact total has been loaded. The refresh icon stays, disabled. */
    countRecalculated?: boolean;
}

/**
 * "Showing … entries" counter above the hit list.
 * Keeps the branches of `search-filter.tpl.html` lines 160-197. Past the
 * count limit the refresh icon loads the exact total, spins while loading,
 * and is disabled once the total is exact. The text truncates first so the
 * icon stays visible.
 */
export function FoundItemsLabel({
    visibleRowCount,
    numberFound,
    maxNumberFound,
    countingItems = false,
    hasCursor = false,
    onRefreshCount,
    countRecalculated = false,
}: FoundItemsLabelProps) {
    const { t } = useTranslation();

    const label = showingEntriesLabel(t, { visibleRowCount, numberFound, maxNumberFound, hasCursor });
    if (!label) {
        return null;
    }

    const showRefresh = Boolean(onRefreshCount)
        && (label.approximate || countingItems || countRecalculated);
    const refreshDisabled = countingItems || countRecalculated;

    return (
        <div
            id="found-items-label"
            className="tw-flex tw-min-w-0 tw-items-center tw-justify-end tw-gap-0.5"
            data-items-found={numberFound}
        >
            <span className="tw-min-w-0 tw-truncate" title={label.text}>
                {label.text}
            </span>
            {showRefresh && (
                <button
                    type="button"
                    onClick={onRefreshCount}
                    disabled={refreshDisabled}
                    aria-busy={countingItems || undefined}
                    className="tw-inline-flex tw-shrink-0 disabled:tw-cursor-default"
                    aria-label={t('ITEM.RECALCULATE_ENTRIES')}
                    title={t('ITEM.RECALCULATE_ENTRIES')}
                >
                    <Icon
                        icon={ArrowPathIcon}
                        size="sm"
                        variant="secondary"
                        disabled={countRecalculated}
                        className={cn(countingItems && 'tw-animate-spin')}
                    />
                </button>
            )}
        </div>
    );
}
